import { useEffect, useState } from 'react'
import NewOrder from './NewOrder'
import { lineItemType, orderType, shipToType } from '../types'
import { useParams } from 'react-router'
import axios from 'axios'
import ErrorScreen from '../Misc/ErrorScreen'
import { loadDocs } from '../helper'
import LoadingCircle from './LoadingCircle.gif'

export type tempFiles = {
  [index: string]: DataTransfer
  GovernmentAward: DataTransfer
  IMPRESQuote: DataTransfer
  VendorQuote: DataTransfer
  RelatedDoc: DataTransfer
}

function Correction({ draft = false }: { draft?: boolean }) {
  const { orderID } = useParams()

  const [order, setOrder] = useState<orderType>({} as orderType)
  const [files, setFiles] = useState<tempFiles>({} as tempFiles)
  const [lineItems, setLineItems] = useState<lineItemType[]>([])
  const [addresses, setAddresses] = useState<(shipToType & { default: boolean; id: number })[]>([])
  const [orderLoad, setOrderLoad] = useState<'loading' | 'error' | ''>('loading')

  const parseAddresses = (order: orderType, addresses: shipToType[]) => {
    const addressResponse = new Set<shipToType>()

    addressResponse.add({
      ShipToAttention: order.ShipToAttention,
      ShipToCompany: order.ShipToCompany,
      ShipToAddress1: order.ShipToAddress1,
      ShipToAddress2: order.ShipToAddress2,
      ShipToAddressCity: order.ShipToAddressCity,
      ShipToAddressState: order.ShipToAddressState,
      ShipToAddressZip: order.ShipToAddressZip,
      ShipToAddressCountry: order.ShipToAddressCountry,
      ReceiverContactName: order.ReceiverContactName,
      ReceiverContactEmail: order.ReceiverContactEmail,
      ReceiverContactPhone: order.ReceiverContactPhone,
    })
    addresses.forEach(address => {
      if (Object.values(address).every(value => value === null)) return
      addressResponse.add({
        ShipToAttention: address.ShipToAttention,
        ShipToCompany: address.ShipToCompany,
        ShipToAddress1: address.ShipToAddress1,
        ShipToAddress2: address.ShipToAddress2,
        ShipToAddressCity: address.ShipToAddressCity,
        ShipToAddressState: address.ShipToAddressState,
        ShipToAddressZip: address.ShipToAddressZip,
        ShipToAddressCountry: address.ShipToAddressCountry,
        ReceiverContactName: address.ReceiverContactName,
        ReceiverContactEmail: address.ReceiverContactEmail,
        ReceiverContactPhone: address.ReceiverContactPhone,
      })
    })

    setAddresses(
      Array.from(addressResponse).map((address, index) => ({
        ...address,
        default: index === 0,
        id: index + 1,
      })),
    )
  }

  useEffect(() => {
    if (draft) {
      axios
        .get(`/order/${orderID}`)
        .then(orderResponse => {
          setOrder({
            ...orderResponse.data.order,
            QuoteDate: orderResponse.data.order.QuoteDate ? new Date(orderResponse.data.order.QuoteDate) : undefined,
            QuoteExpires: orderResponse.data.order.QuoteExpires
              ? new Date(orderResponse.data.order.QuoteExpires)
              : undefined,
            DeliveryDueDate: orderResponse.data.order.DeliveryDueDate
              ? new Date(orderResponse.data.order.DeliveryDueDate)
              : undefined,
            AwardDate: orderResponse.data.order.AwardDate ? new Date(orderResponse.data.order.AwardDate) : undefined,
          })
          setLineItems(
            orderResponse.data.lineItems.sort((a: lineItemType, b: lineItemType) => (a.Number ?? 0) - (b.Number ?? 0)),
          )
          parseAddresses(
            orderResponse.data.order,
            orderResponse.data.lineItems.map((lineItem: lineItemType) => ({
              ShipToAttention: lineItem.ShipToAttention,
              ShipToCompany: lineItem.ShipToCompany,
              ShipToAddress1: lineItem.ShipToAddress1,
              ShipToAddress2: lineItem.ShipToAddress2,
              ShipToAddressCity: lineItem.ShipToAddressCity,
              ShipToAddressState: lineItem.ShipToAddressState,
              ShipToAddressZip: lineItem.ShipToAddressZip,
              ShipToAddressCountry: lineItem.ShipToAddressCountry,
              ReceiverContactName: lineItem.ReceiverContactName,
              ReceiverContactEmail: lineItem.ReceiverContactEmail,
              ReceiverContactPhone: lineItem.ReceiverContactPhone,
            })),
          )
          loadDocs(orderResponse.data.order.Id)
            .then(files => {
              setFiles(files)
              setOrderLoad('')
            })
            .catch(err => {
              setOrderLoad('error')
              console.error(err)
            })
        })
        .catch(err => {
          setOrderLoad('error')
          console.error(err)
        })
    } else {
      axios
        .get(`/rejected/${orderID}`)
        .then(orderResponse => {
          setOrder({
            ...orderResponse.data.order,
            QuoteDate: new Date(orderResponse.data.order.QuoteDate),
            QuoteExpires: new Date(orderResponse.data.order.QuoteExpires),
            DeliveryDueDate: new Date(orderResponse.data.order.DeliveryDueDate),
            AwardDate: new Date(orderResponse.data.order.AwardDate),
          })
          setLineItems(
            orderResponse.data.lineItems.sort((a: lineItemType, b: lineItemType) => (a.Number ?? 0) - (b.Number ?? 0)),
          )

          parseAddresses(orderResponse.data.order, orderResponse.data.addresses)

          if (orderResponse.data.order.Id) {
            axios
              .get(`/listDocuments/${orderResponse.data.order.Id}`)
              .then(fileResponse => {
                const files: { [index: string]: DataTransfer } = {
                  GovernmentAward: new DataTransfer(),
                  IMPRESQuote: new DataTransfer(),
                  VendorQuote: new DataTransfer(),
                  RelatedDoc: new DataTransfer(),
                }

                const promises = []

                for (const docType in fileResponse.data) {
                  for (const doc of fileResponse.data[docType]) {
                    const docNameUrl = (doc.name as string).replaceAll('#', '%23')
                    promises.push(
                      axios
                        .get(`/sharepointContents/${orderResponse.data.order.Id}/${docType}/${docNameUrl}`, {
                          responseType: 'blob',
                        })
                        .then(fileContentResponse => {
                          files[docType].items.add(new File([fileContentResponse.data], doc.name))
                        })
                        .catch(err => {
                          if (err.response) {
                            // received error code other than 2xx
                            console.error(err.response)
                          } else if (err.request) {
                            // no response received
                            console.error(err.request)
                          } else {
                            // request was not made
                            console.error(err.message)
                          }
                          throw err
                        }),
                    )
                  }
                }

                Promise.all(promises)
                  .then(() => {
                    setFiles(files as tempFiles)
                    setOrderLoad('')
                  })
                  .catch(() => {
                    setOrderLoad('error')
                  })
              })
              .catch(err => {
                setOrderLoad('error')
                if (err.response) {
                  // received error code other than 2xx
                  console.error(err.response)
                } else if (err.request) {
                  // no response received
                  console.error(err.request)
                } else {
                  // request was not made
                  console.error(err.message)
                }
              })
          }
        })
        .catch(err => {
          setOrderLoad('error')
          if (err.response) {
            // received error code other than 2xx
            console.error(err.response)
          } else if (err.request) {
            // no response received
            console.error(err.request)
          } else {
            // request was not made
            console.error(err.message)
          }
        })
    }
  }, [])

  return (
    <>
      {orderLoad === 'error' ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 102px)' }}>
          <ErrorScreen
            title="Error Loading Order"
            message="There was a problem loading the order. Please try again. If the problem persists, please contact support."
          />
        </div>
      ) : orderLoad === 'loading' ? (
        <div
          style={{
            background: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 102px)',
          }}
        >
          <img src={LoadingCircle} alt="Loading" style={{ width: '150px', height: '150px' }} />
        </div>
      ) : (
        <NewOrder originalOrder={order} originalFiles={files} originalLines={lineItems} originalAddresses={addresses} />
      )}
    </>
  )
}

export default Correction
